import React, { useContext, useState, useEffect, useRef } from "react";
import Paper from "@material-ui/core/Paper";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import { Button } from "@material-ui/core";

import SpeedIcon from "@material-ui/icons/Speed";
import GroupIcon from "@material-ui/icons/Group";
import AssignmentIcon from "@material-ui/icons/Assignment";
import PersonIcon from "@material-ui/icons/Person";
import CallIcon from "@material-ui/icons/Call";
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import StoreIcon from '@material-ui/icons/Store';
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ForumIcon from "@material-ui/icons/Forum";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import SendIcon from "@material-ui/icons/Send";
import MessageIcon from "@material-ui/icons/Message";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import TimerIcon from "@material-ui/icons/Timer";

import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";
import { toast } from "react-toastify";

import Chart from "./Chart";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import CardCounter from "../../components/Dashboard/CardCounter";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { isArray } from "lodash";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

import useDashboard from "../../hooks/useDashboard";
import useTickets from "../../hooks/useTickets";
import useUsers from "../../hooks/useUsers";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";

import Filters from "./Filters";
import { isEmpty } from "lodash";
import moment from "moment";
import { ChartsDate } from "./ChartsDate";
import ChartsAppointmentsAtendent from "./ChartsAppointmentsAtendent";
import ChartsRushHour from "./ChartsRushHour";
import ChartsDepartamentRatings from "./ChartsDepartamentRatings";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    backgroundColor: theme.palette.type === "dark" ? "#121212" : "#F8FAFC",
    minHeight: "100vh",
  },
  card: {
    borderRadius: theme.shape.borderRadius * 3,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: 140,
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.08)",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.12)",
      "& $cardIcon": {
        transform: "scale(1.1) rotate(-5deg)",
        opacity: 0.25,
      }
    },
  },
  cardBlue: {
    background: "linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardGreen: {
    background: "linear-gradient(135deg, #4CAF50 0%, #1B5E20 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardOrange: {
    background: "linear-gradient(135deg, #FF9800 0%, #E65100 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardPurple: {
    background: "linear-gradient(135deg, #9C27B0 0%, #4A148C 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardIndigo: {
    background: "linear-gradient(135deg, #3F51B5 0%, #1A237E 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardTeal: {
    background: "linear-gradient(135deg, #009688 0%, #004D40 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardRose: {
    background: "linear-gradient(135deg, #E91E63 0%, #880E4F 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardRed: {
    background: "linear-gradient(135deg, #F44336 0%, #B71C1C 100%)",
    "& $cardTitle, & $cardValue, & $cardIcon": { color: "#fff" },
  },
  cardContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    zIndex: 1,
  },
  cardText: {
    flex: 1,
  },
  cardTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    opacity: 0.9,
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontSize: "2.2rem",
    fontWeight: 800,
    lineHeight: 1.1,
  },
  cardIcon: {
    fontSize: "4.5rem",
    position: "absolute",
    right: -10,
    bottom: -10,
    opacity: 0.15,
    transition: "all 0.4s ease",
  },
  fixedHeightPaper: {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 3,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  filterSection: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#fff",
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    backdropFilter: "blur(8px)",
  },
  filterButton: {
    height: "40px",
    borderRadius: theme.shape.borderRadius * 1.5,
    fontWeight: 700,
    textTransform: "none",
    fontSize: "0.95rem",
    padding: theme.spacing(0, 4),
    boxShadow: `0 4px 12px 0 ${theme.palette.primary.main}44`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: `0 6px 16px 0 ${theme.palette.primary.main}66`,
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    }
  },
}));

const Dashboard = () => {
  const classes = useStyles();

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [period, setPeriod] = useState(0);
  const [filterType, setFilterType] = useState(1);
  const [dateFrom, setDateFrom] = useState(moment("1", "D").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function firstLoad() {
      await fetchData();
    }
    setTimeout(() => {
      firstLoad();
    }, 1000);
  }, []);

  async function handleChangePeriod(value) {
    setPeriod(value);
  }

  async function handleChangeFilterType(value) {
    setFilterType(value);
    if (value === 1) {
      setPeriod(0);
    } else {
      setDateFrom("");
      setDateTo("");
    }
  }

  async function fetchData() {
    setLoading(true);

    let params = {};

    if (period > 0) {
      params = { days: period };
    }

    if (!isEmpty(dateFrom) && moment(dateFrom).isValid()) {
      params = { ...params, date_from: moment(dateFrom).format("YYYY-MM-DD") };
    }

    if (!isEmpty(dateTo) && moment(dateTo).isValid()) {
      params = { ...params, date_to: moment(dateTo).format("YYYY-MM-DD") };
    }

    if (Object.keys(params).length === 0) {
      toast.error(i18n.t("dashboard.messages.parameterizeFilter"));
      setLoading(false);
      return;
    }

    const data = await find(params);
    setCounters(data.counters);
    setAttendants(isArray(data.attendants) ? data.attendants : []);
    setLoading(false);
  }

  function formatTime(minutes) {
    return moment()
      .startOf("day")
      .add(minutes, "minutes")
      .format("HH[h] mm[m]");
  }

  const GetUsers = () => {
    return attendants.filter(user => user.online).length;
  };

  const GetContacts = (all) => {
    let props = all ? {} : {};
    const { count } = useContacts(props);
    return count;
  };

  function renderFilters() {
    if (filterType === 1) {
      return (
        <>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label={i18n.t("dashboard.filters.dateFrom")}
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label={i18n.t("dashboard.filters.dateTo")}
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Grid>
        </>
      );
    } else {
      return (
        <Grid item xs={12} sm={6} md={6}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="period-selector-label">{i18n.t("dashboard.filters.period")}</InputLabel>
            <Select
              labelId="period-selector-label"
              value={period}
              onChange={(e) => handleChangePeriod(e.target.value)}
              label={i18n.t("dashboard.filters.period")}
            >
              <MenuItem value={0}>{i18n.t("dashboard.filters.noneSelected")}</MenuItem>
              <MenuItem value={3}>{i18n.t("dashboard.filters.last3Days")}</MenuItem>
              <MenuItem value={7}>{i18n.t("dashboard.filters.last7Days")}</MenuItem>
              <MenuItem value={15}>{i18n.t("dashboard.filters.last15Days")}</MenuItem>
              <MenuItem value={30}>{i18n.t("dashboard.filters.last30Days")}</MenuItem>
              <MenuItem value={60}>{i18n.t("dashboard.filters.last60Days")}</MenuItem>
              <MenuItem value={90}>{i18n.t("dashboard.filters.last90Days")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      );
    }
  }

  return (
    <Container maxWidth="xl" className={classes.container}>
      <Grid container spacing={3}>
        {/* Cards Section */}
        <Grid container item xs={12} spacing={3}>
          {/* Conexões Ativas */}
          {user.super && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper className={`${classes.card} ${classes.cardBlue}`}>
                <div className={classes.cardContent}>
                  <div className={classes.cardText}>
                    <Typography variant="subtitle2" className={classes.cardTitle}>
                      {i18n.t("dashboard.cards.activeConnections")}
                    </Typography>
                    <Typography variant="h4" className={classes.cardValue}>
                      {counters.totalWhatsappSessions || 0}
                    </Typography>
                  </div>
                  <MobileFriendlyIcon className={classes.cardIcon} />
                </div>
              </Paper>
            </Grid>
          )}

          {/* Empresas */}
          {user.super && (
            <Grid item xs={12} sm={6} md={3}>
              <Paper className={`${classes.card} ${classes.cardIndigo}`}>
                <div className={classes.cardContent}>
                  <div className={classes.cardText}>
                    <Typography variant="subtitle2" className={classes.cardTitle}>
                      {i18n.t("dashboard.cards.companies")}
                    </Typography>
                    <Typography variant="h4" className={classes.cardValue}>
                      {counters.totalCompanies || 0}
                    </Typography>
                  </div>
                  <StoreIcon className={classes.cardIcon} />
                </div>
              </Paper>
            </Grid>
          )}

          {/* Em Conversa */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={`${classes.card} ${classes.cardPurple}`}>
              <div className={classes.cardContent}>
                <div className={classes.cardText}>
                  <Typography variant="subtitle2" className={classes.cardTitle}>
                    {i18n.t("dashboard.cards.inConversation")}
                  </Typography>
                  <Typography variant="h4" className={classes.cardValue}>
                    {counters.supportHappening || 0}
                  </Typography>
                </div>
                <CallIcon className={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Aguardando */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={`${classes.card} ${classes.cardOrange}`}>
              <div className={classes.cardContent}>
                <div className={classes.cardText}>
                  <Typography variant="subtitle2" className={classes.cardTitle}>
                    {i18n.t("dashboard.cards.waiting")}
                  </Typography>
                  <Typography variant="h4" className={classes.cardValue}>
                    {counters.supportPending || 0}
                  </Typography>
                </div>
                <HourglassEmptyIcon className={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Novos Contatos */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={`${classes.card} ${classes.cardTeal}`}>
              <div className={classes.cardContent}>
                <div className={classes.cardText}>
                  <Typography variant="subtitle2" className={classes.cardTitle}>
                    {i18n.t("dashboard.cards.newContacts")}
                  </Typography>
                  <Typography variant="h4" className={classes.cardValue}>
                    {GetContacts(true) || 0}
                  </Typography>
                </div>
                <GroupAddIcon className={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* T.M. de Conversa */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={`${classes.card} ${classes.cardRose}`}>
              <div className={classes.cardContent}>
                <div className={classes.cardText}>
                  <Typography variant="subtitle2" className={classes.cardTitle}>
                    {i18n.t("dashboard.cards.avgConversationTime")}
                  </Typography>
                  <Typography variant="h4" className={classes.cardValue}>
                    {formatTime(counters.avgSupportTime || 0)}
                  </Typography>
                </div>
                <AccessAlarmIcon className={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Finalizados */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={`${classes.card} ${classes.cardGreen}`}>
              <div className={classes.cardContent}>
                <div className={classes.cardText}>
                  <Typography variant="subtitle2" className={classes.cardTitle}>
                    {i18n.t("dashboard.cards.finished")}
                  </Typography>
                  <Typography variant="h4" className={classes.cardValue}>
                    {counters.supportFinished || 0}
                  </Typography>
                </div>
                <CheckCircleIcon className={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* T.M. de Espera */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={`${classes.card} ${classes.cardRed}`}>
              <div className={classes.cardContent}>
                <div className={classes.cardText}>
                  <Typography variant="subtitle2" className={classes.cardTitle}>
                    {i18n.t("dashboard.cards.avgWaitTime")}
                  </Typography>
                  <Typography variant="h4" className={classes.cardValue}>
                    {formatTime(counters.avgWaitTime || 0)}
                  </Typography>
                </div>
                <TimerIcon className={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters Section */}
        <Grid item xs={12}>
          <Paper className={classes.filterSection}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="filter-type-label">{i18n.t("dashboard.filters.filterType")}</InputLabel>
                  <Select
                    labelId="filter-type-label"
                    value={filterType}
                    onChange={(e) => handleChangeFilterType(e.target.value)}
                    label={i18n.t("dashboard.filters.filterType")}
                  >
                    <MenuItem value={1}>{i18n.t("dashboard.filters.dateFilter")}</MenuItem>
                    <MenuItem value={2}>{i18n.t("dashboard.filters.periodFilter")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {renderFilters()}

              <Grid item xs={12} sm={6} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={fetchData}
                  disabled={loading}
                  className={classes.filterButton}
                  startIcon={<FilterListIcon />}
                >
                  {i18n.t("dashboard.filters.filter")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Attendants Status */}
        {attendants.length > 0 && (
          <Grid item xs={12}>
            <TableAttendantsStatus attendants={attendants} loading={loading} />
          </Grid>
        )}

        {/* Charts Section */}
        <Grid item xs={12}>
          <Paper className={classes.fixedHeightPaper}>
            <ChatsUser />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper className={classes.fixedHeightPaper}>
            <ChartsDate />
          </Paper>
        </Grid>

        <ChartsAppointmentsAtendent />
        <ChartsRushHour />
        <ChartsDepartamentRatings />
      </Grid>
    </Container>
  );
};

export default Dashboard;
